import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle } from "lucide-react";

const pretty = s => s ? s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "";

export default function BrandApprovalsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("brands");
  const [brands, setBrands] = useState([]);
  const [changeReqs, setChangeReqs] = useState([]);
  const [rejectInput, setRejectInput] = useState({});

  const load = () => {
    api.get("/brands?lifecycle=submitted").then(r => setBrands(r.data)).catch(() => {});
    api.get("/brands/change-requests?status=pending").then(r => setChangeReqs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => { try { await api.post(`/brands/${id}/approve`); toast.success("Approved"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const reject = async (id) => {
    const reason = rejectInput[id] || "";
    if (!reason) { toast.error("Reason required"); return; }
    try { await api.post(`/brands/${id}/reject`, { reason }); toast.success("Rejected"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const sendBack = async (id) => {
    const note = rejectInput[id] || "";
    if (!note) { toast.error("Note required"); return; }
    try { await api.post(`/brands/${id}/send-back`, { note }); toast.success("Sent back"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const approveCR = async (crId) => { try { await api.post(`/brands/change-requests/${crId}/approve`); toast.success("Change approved"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const rejectCR = async (crId) => {
    const note = rejectInput[crId] || "";
    try { await api.post(`/brands/change-requests/${crId}/reject`, { note }); toast.success("Change rejected"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="brand-approvals-page">
      <h1 className="text-xl font-bold">Brand Approvals</h1>
      <div className="flex gap-2">
        <button onClick={() => setTab("brands")} className={`px-3 py-1 rounded-full text-xs font-medium ${tab === "brands" ? "bg-zinc-900 text-white" : "bg-zinc-100"}`}>New Brands ({brands.length})</button>
        <button onClick={() => setTab("changes")} className={`px-3 py-1 rounded-full text-xs font-medium ${tab === "changes" ? "bg-zinc-900 text-white" : "bg-zinc-100"}`}>Change Requests ({changeReqs.length})</button>
      </div>
      {tab === "brands" && brands.map(b => (
        <div key={b._id} className="border rounded-md p-4" data-testid={`approval-brand-${b._id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="cursor-pointer" onClick={() => navigate(`/brands/${b._id}`)}>
              <span className="font-semibold text-sm">{b.display_name}</span>
              <span className="ml-2 text-xs text-zinc-500">{pretty((b.identity || {}).owner_type || "")}</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-emerald-600" onClick={() => approve(b._id)}><CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve</Button>
              <Button size="sm" variant="destructive" onClick={() => reject(b._id)}><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
              <Button size="sm" variant="outline" onClick={() => sendBack(b._id)}>Send Back</Button>
            </div>
          </div>
          <Input className="h-7 text-xs" placeholder="Reason / note (required for reject/send-back)" value={rejectInput[b._id] || ""} onChange={e => setRejectInput({...rejectInput, [b._id]: e.target.value})} />
        </div>
      ))}
      {tab === "changes" && changeReqs.map(cr => (
        <div key={cr._id} className="border rounded-md p-4" data-testid={`approval-cr-${cr._id}`}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className="font-semibold text-sm">{cr.brand_display_name}</span>
              <span className="ml-2 text-xs text-zinc-500">by {cr.requested_by_name} — {cr.reason}</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="bg-emerald-600" onClick={() => approveCR(cr._id)}>Approve</Button>
              <Button size="sm" variant="destructive" onClick={() => rejectCR(cr._id)}>Reject</Button>
            </div>
          </div>
          {Object.entries(cr.changes || {}).map(([field, vals]) => (
            <div key={field} className="text-xs border-l-2 border-blue-200 pl-2 mb-1">
              <span className="font-medium">{pretty(field)}:</span> <span className="text-red-500 line-through">{String(vals.old || "—")}</span> <span className="mx-1">→</span> <span className="text-emerald-600">{String(vals.new || "—")}</span>
            </div>
          ))}
          <Input className="h-7 text-xs mt-1" placeholder="Note (optional)" value={rejectInput[cr._id] || ""} onChange={e => setRejectInput({...rejectInput, [cr._id]: e.target.value})} />
        </div>
      ))}
    </div>
  );
}
